import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

/**
 * Max consecutive AUTO-fills allowed WITHOUT the container achieving overflow,
 * before the hook stops and hands off to a manual "Load more" affordance.
 *
 * Rationale: the webpage query page size is `limit: 100` (useWebpages.ts:54), so
 * 5 rounds ≈ 500 rows auto-loaded — far more than any viewport needs to overflow
 * (the normal case fills in 1–2 rounds), while BOUNDING the pathological
 * "Not Viewed" case where the first unviewed item is thousands of viewed items
 * deep on a million-row surface. Past the cap, the user gets a visible "Load
 * more" button (a human brake), instead of the hook serially draining the whole
 * dataset into memory. Tunable in one place.
 */
export const AUTO_FILL_MAX_ROUNDS = 5;

interface UseAutoFillNextPageArgs {
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  /** Gate the whole hook (e.g. only in grid view / only when status==='success'). */
  enabled?: boolean;
  /**
   * Values that change RENDERED height WITHOUT re-arming the cap (a page arrived,
   * loaded length grew). Changing it re-measures overflow. Primitives only.
   */
  contentKey?: unknown;
  /**
   * Values that RE-ARM auto-fill from round 0 — a user-driven filter/sort change
   * (e.g. the viewed-filter value). Kept DISTINCT from `contentKey` so page
   * arrivals never reset the consecutive-fill counter (only a deliberate filter
   * change or a manual `loadMore` does). Primitives only.
   */
  resetKey?: unknown;
}

interface UseAutoFillNextPageResult {
  /** True once the consecutive-auto-fill cap is hit while `hasNextPage` is still
   *  true — the consumer MUST render a manual "Load more" affordance. */
  capReached: boolean;
  /** Manual brake release: reset the round counter and fetch ONE more page.
   *  Auto-fill then resumes for up to AUTO_FILL_MAX_ROUNDS more rounds. */
  loadMore: () => void;
}

/**
 * Pages `fetchNextPage` while the scroll container has NO overflow and
 * `hasNextPage` is still true — so a client-side filter that shrinks the rendered
 * list below the container height cannot strand pagination (the passive
 * `useScroll` listener needs overflow to ever fire). Bounded by
 * `AUTO_FILL_MAX_ROUNDS` consecutive auto-fills; past the cap it stops and
 * surfaces a manual "Load more" affordance (`capReached` + `loadMore`).
 */
export function useAutoFillNextPage(
  scrollRef: RefObject<HTMLElement | null>,
  {
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    enabled = true,
    contentKey,
    resetKey,
  }: UseAutoFillNextPageArgs
): UseAutoFillNextPageResult {
  const roundsRef = useRef(0);
  const [capReached, setCapReached] = useState(false);

  // Re-arm on a user-driven filter/sort change (NOT on page arrival). INVARIANT
  // (plan File #4): the round counter and the cap flag MUST reset ATOMICALLY in
  // one effect. Do NOT split the cap-flag reset into a render-phase adjustment —
  // that skews the two resets across the pre-paint ResizeObserver, which can then
  // observe `capReached=false` with a stale `roundsRef>=MAX` and briefly re-cap
  // (flashing "Load more" mid-auto-fetch). Resetting both here keeps them
  // correct-by-construction.
  useEffect(() => {
    roundsRef.current = 0;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- atomic re-arm of derived auto-fill state keyed off the filter/sort signature; reset together with roundsRef so the two never skew.
    setCapReached(false);
  }, [resetKey]);

  const maybeFetch = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return; // container ref not mounted yet
    if (!enabled) return;
    if (hasNextPage !== true) return; // nothing more to load
    if (isFetchingNextPage) return; // a page is already in flight
    if (el.scrollHeight > el.clientHeight + 1) {
      // 1px tolerance for subpixel
      // Overflow achieved ⇒ the passive useScroll (human-scroll) path takes
      // over. Reset so a later filter shrink re-arms auto-fill fresh.
      roundsRef.current = 0;
      if (capReached) setCapReached(false);
      return;
    }
    // No overflow AND more pages remain.
    if (roundsRef.current >= AUTO_FILL_MAX_ROUNDS) {
      if (!capReached) setCapReached(true); // hand off to manual "Load more"
      return;
    }
    roundsRef.current += 1;
    fetchNextPage?.();
  }, [
    scrollRef,
    enabled,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    capReached,
  ]);

  const loadMore = useCallback(() => {
    roundsRef.current = 0;
    setCapReached(false);
    if (hasNextPage === true && !isFetchingNextPage) fetchNextPage?.();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Re-check after every state change that could leave the list too short.
  useEffect(() => {
    if (!enabled) return; // opted-out consumers do NOTHING
    // eslint-disable-next-line react-hooks/set-state-in-effect -- `maybeFetch` MEASURES the DOM scroll container (scrollHeight/clientHeight) after render and only sets the cap flag downstream of that layout read — the sanctioned "synchronize with an external system (DOM layout)" effect use, not derived-from-props state.
    maybeFetch();
  }, [enabled, maybeFetch, contentKey]);

  // Re-check on container OR content resize (viewport change, content grows).
  useEffect(() => {
    if (!enabled) return; // MEDIUM fix: attach NOTHING when disabled
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => maybeFetch());
    ro.observe(el);
    for (const child of Array.from(el.children)) ro.observe(child);
    return () => ro.disconnect(); // unmount cleanup
  }, [enabled, scrollRef, maybeFetch, contentKey]);

  return { capReached, loadMore };
}
